// ===========================================================================
// Quick BGV — the link contract between the candidate page and the BGV page
// ===========================================================================
//
// Two screens have to agree on one thing: how a candidate is handed from
// "Actions -> Quick BGV" on /ats/candidates/:id to /ats/bgv. Both sides import
// this module so that agreement lives in one file rather than in two string
// literals that can drift apart.
//
// THE CONTRACT
// ------------
//   /ats/bgv?candidate=<candidate id>          <- the whole contract
//
// The id in the query string is the ONLY load-bearing part. Everything else
// (the candidate's name, email, employers) travels in React Router's location
// state as a convenience so the panel can name the person before any request
// comes back. State is a nicety and is treated as one:
//
//   * it survives a reload in the same tab (the browser restores the history
//     entry's state object), which covers the accidental F5;
//   * it does NOT survive a link pasted into a fresh tab, so the BGV panel
//     re-resolves everything from the server anyway and never depends on it;
//   * it is never sent to the server. Opening the case sends the id and
//     nothing else, so a stale or hand-edited state object cannot write a
//     wrong name into a BGV case.
//
// WHY A LINK AND NOT AN ACTION
// ----------------------------
// Opening a BGV case emails the candidate a document request and starts a
// 48-hour clock. routes/bgv_intake.py deliberately reserves that moment for an
// admin. So the menu item navigates; the BGV page — which already knows the
// signed-in role and already refuses the wrong one — decides what the button
// on the other end does. That keeps one implementation of "open a case", which
// is the same reason create_case_core() was lifted to module level.

export const QUICK_BGV_PATH = '/ats/bgv';
export const QUICK_BGV_PARAM = 'candidate';

// Where the candidate's own page lives, so the panel can send someone back to
// fill in a missing field. Both ATS routes for a candidate render the same
// page; this is the one inside the admin layout.
export const CANDIDATE_PATH = '/ats/candidates';

/** A UUID as this ATS writes them (candidate ids, case ids, requirement ids). */
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Pull a candidate id out of a path like /ats/candidates/<uuid>.
 *
 *  useParams() is the right answer and is tried first by the caller. This is
 *  the belt to that pair of braces: the app mounts the candidate page on two
 *  routes (/ats/candidates/:id and the public /candidate/:id) and a third could
 *  appear. Reading the path costs nothing and cannot go stale.
 */
export function candidateIdFromPath(pathname) {
  const match = UUID_RE.exec(String(pathname || ''));
  if (match) return match[0];
  // Not every id in this database is a UUID — a few older rows carry a short
  // code — so fall back to the last non-empty path segment, which is where the
  // id sits on every route that renders a candidate.
  const parts = String(pathname || '').split('/').filter(Boolean);
  const last = parts[parts.length - 1] || '';
  return /^[\w-]{6,}$/.test(last) ? last : '';
}

/** First non-empty value among several possible field names.
 *
 *  Field names in this database are spelled several ways for the same fact —
 *  `name`/`full_name`, `phone`/`mobile`, `current_designation`/`designation` —
 *  because records arrive from the JM import, the CV parser and the ATS's own
 *  form. Reading only one spelling is what has already blanked out requirement
 *  titles and match scores elsewhere in this app, so nothing here reads one.
 */
function pick(source, fields) {
  for (const field of fields) {
    const value = source ? source[field] : undefined;
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

/** The employers already on file, most recent first, de-duplicated.
 *
 *  Shown on the panel so the person starting the case can see the ATS already
 *  holds an employment history to verify — which is the whole point of Quick
 *  BGV over the old screen, where every employer had to be typed in by hand.
 */
function employersOf(candidate) {
  const lists = ['work_experience', 'experience', 'employment_history',
    'work_history', 'employments', 'past_employers'];
  const out = [];
  const seen = new Set();

  const add = (value) => {
    const text = String(value || '').trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(text);
  };

  add(pick(candidate, ['current_company', 'current_employer', 'company']));

  for (const listName of lists) {
    const rows = candidate ? candidate[listName] : null;
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (typeof row === 'string') { add(row); continue; }
      add(pick(row, ['company', 'company_name', 'employer', 'organisation',
        'organization', 'name']));
    }
  }
  return out.slice(0, 6);
}

/** Everything the ATS already knows about this person, in one flat object.
 *
 *  Built on the candidate page, where the full record is already in memory, so
 *  the BGV panel can render a filled-in card on its very first paint instead of
 *  a spinner. It is display data only — see the contract note at the top.
 */
export function buildHandoff(candidate) {
  if (!candidate || typeof candidate !== 'object') return null;
  const id = pick(candidate, ['id', '_id', 'candidate_id']);
  if (!id) return null;
  return {
    id,
    name: pick(candidate, ['name', 'full_name', 'candidate_name']),
    email: pick(candidate, ['email', 'email_address', 'personal_email']),
    phone: pick(candidate, ['phone', 'mobile', 'phone_number', 'contact_number']),
    designation: pick(candidate, ['current_designation', 'designation',
      'current_role', 'job_title']),
    company: pick(candidate, ['current_company', 'current_employer', 'company']),
    location: pick(candidate, ['current_location', 'location', 'city']),
    code: pick(candidate, ['candidate_code', 'resume_number']),
    status: pick(candidate, ['status', 'stage', 'pipeline_status']),
    requirementId: pick(candidate, ['requirement_id']),
    employers: employersOf(candidate),
  };
}

/** The navigation target for "Quick BGV", ready to hand to navigate().
 *
 *  `candidate` is optional: when the caller has the record it becomes the
 *  handoff, and when it does not the link still works because the id in the
 *  query string is all the far end actually needs.
 */
export function quickBgvTo(candidateId, candidate) {
  const id = String(candidateId || '').trim();
  const handoff = buildHandoff(candidate);
  return {
    to: {
      pathname: QUICK_BGV_PATH,
      search: `?${QUICK_BGV_PARAM}=${encodeURIComponent(id)}`,
    },
    options: { state: handoff ? { quickBgv: handoff } : undefined },
  };
}
